// src/pages/maintenance/WorkOrderModal.js
// Single-step work order creation — no separate "work request" approval stage, per the module's
// design. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState } from 'react';
import maintenanceService from '../../services/maintenanceService';
import userService from '../../services/userService';
import SearchableSelect from '../../components/SearchableSelect';
import VendorPicker from './VendorPicker';

const TYPE_OPTIONS = ['reactive', 'preventive', 'inspection', 'calibration', 'safety'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function WorkOrderModal({ defaultAssetId, onSave, onClose }) {
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', type: 'reactive', priority: 'medium',
    assetId: defaultAssetId || '', assignedToUserId: '', vendorId: '',
    vendorTechnicianName: '', dueAt: '', estimatedHours: '', isCalibrationWo: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      maintenanceService.getAssets(),
      userService.listUsers(),
    ]).then(([a, u]) => { setAssets(a); setUsers(u); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        assetId: form.assetId || null,
        assignedToUserId: form.assignedToUserId || null,
        vendorId: form.vendorId || null,
        estimatedHours: form.estimatedHours !== '' ? parseFloat(form.estimatedHours) : null,
        dueAt: form.dueAt || null,
      };
      const wo = await maintenanceService.createWorkOrder(payload);
      onSave(wo);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create work order.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">New Work Order</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Title <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Oven thermostat not holding temp" />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Description</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Asset</label>
                  <SearchableSelect
                    options={assets.map(a => ({ value: a.id, label: a.name }))}
                    value={form.assetId}
                    onChange={val => set('assetId', val || '')}
                    placeholder="No specific asset"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Type</label>
                  <SearchableSelect
                    options={TYPE_OPTIONS.map(t => ({ value: t, label: labelize(t) }))}
                    value={form.type}
                    onChange={val => set('type', val || 'reactive')}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Priority</label>
                  <SearchableSelect
                    options={PRIORITY_OPTIONS.map(p => ({ value: p, label: labelize(p) }))}
                    value={form.priority}
                    onChange={val => set('priority', val || 'medium')}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Assign To</label>
                  <SearchableSelect
                    options={users.map(u => ({ value: u.id, label: u.username || `${u.firstName || ''} ${u.lastName || ''}`.trim() }))}
                    value={form.assignedToUserId}
                    onChange={val => set('assignedToUserId', val || '')}
                    placeholder="Unassigned"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Vendor (if outside service)</label>
                  <VendorPicker value={form.vendorId} onChange={val => set('vendorId', val || '')} />
                </div>
                {form.vendorId && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Technician Name</label>
                    <input className="form-control form-control-sm" value={form.vendorTechnicianName} onChange={e => set('vendorTechnicianName', e.target.value)} />
                  </div>
                )}
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Due Date</label>
                  <input type="date" className="form-control form-control-sm" value={form.dueAt} onChange={e => set('dueAt', e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Est. Hours</label>
                  <input className="form-control form-control-sm" value={form.estimatedHours} onChange={e => set('estimatedHours', e.target.value)} />
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="isCalib" checked={form.isCalibrationWo} onChange={e => set('isCalibrationWo', e.target.checked)} />
                    <label className="form-check-label" htmlFor="isCalib" style={{ fontSize: 13 }}>
                      This is a calibration work order (generates a locked, signed certificate on completion)
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Creating…' : 'Create Work Order'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
